/**
 * ID-token vooraf verversen, zodat een klik nooit op de token-server hoeft te wachten.
 *
 * Waarom: authFetch stuurt het ID-token mee (niet het access-token). MSAL 3 geeft bij
 * acquireTokenSilent het gecachte ID-token terug zolang het ACCESS-token geldig is — ook als
 * het ID-token (1 uur) al (bijna) verlopen is. authHelpers ziet dat en forceert dan een
 * verversing; dat gebeurde pas bij de eerste API-aanvraag van een nieuwe pagina, dus
 * wachtte die pagina op login.microsoftonline.com (bij een iframe-verversing ~2 s; wiki
 * 27 sep: projectpagina begon pas ~2,1 s na de klik te laden).
 *
 * Nu: elke paar minuten, en direct als het tabblad weer zichtbaar wordt of focus krijgt,
 * stil verversen zodra het ID-token binnen VOORAF_SEC verloopt. Nooit een popup of
 * redirect vanuit de achtergrond — lukt het stil niet, dan doet de gewone route
 * (authHelpers) wat hij altijd deed.
 */
import { getMsalInstance } from './msalSingleton';
import { loginRequest } from '../authConfig';
import { isTokenExpired } from './authHelpers';

// Ruimer dan de 5 min-buffer in authHelpers: zo is er altijd al ververst vóór die toeslaat.
export const VOORAF_SEC = 15 * 60;
export const INTERVAL_MS = 4 * 60 * 1000;

const bezig = new Map(); // homeAccountId → lopende verversing (per account, nooit gedeeld)
let actieveWacht = null; // id van de lopende wacht; null = gestopt
let wachtTeller = 0;

/**
 * Ververst het ID-token van het actieve account stil als het binnen VOORAF_SEC verloopt.
 * `uitWacht`: aangeroepen door de wacht → na stoppen geen vervolgaanvraag meer (een
 * herstarte wacht telt wel: de status wordt bij de tweede stap gelezen, niet bij de start).
 * Geeft true bij verversen.
 */
export function verversIdTokenVooraf({ uitWacht = false } = {}) {
  const msal = getMsalInstance();
  const account = msal?.getActiveAccount?.();
  if (!account) return Promise.resolve(false);
  const sleutel = account.homeAccountId;
  if (bezig.has(sleutel)) return bezig.get(sleutel);
  // Mag de tweede (netwerk)stap nog? Wacht loopt nog én hetzelfde account is actief.
  const magDoor = () => (!uitWacht || actieveWacht !== null)
    && msal.getActiveAccount?.()?.homeAccountId === sleutel;
  const p = (async () => {
    try {
      const r = await msal.acquireTokenSilent({ ...loginRequest, account });
      if (r?.idToken && !isTokenExpired(r.idToken, VOORAF_SEC)) return false;
      if (!magDoor()) return false;
      await msal.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
      return true;
    } catch {
      return false; // interactie nodig of netwerk weg: de gewone route handelt het af
    }
  })().finally(() => { bezig.delete(sleutel); });
  bezig.set(sleutel, p);
  return p;
}

/** Start de wacht (interval + focus + zichtbaar); geeft een stopfunctie terug. */
export function startIdTokenWacht() {
  if (typeof window === 'undefined') return () => {};
  wachtTeller += 1;
  const deze = wachtTeller;
  actieveWacht = deze;
  const ververs = () => verversIdTokenVooraf({ uitWacht: true });
  const bijZicht = () => { if (document.visibilityState === 'visible') ververs(); };
  const interval = setInterval(ververs, INTERVAL_MS);
  document.addEventListener('visibilitychange', bijZicht);
  window.addEventListener('focus', ververs);
  ververs();
  return () => {
    if (actieveWacht === deze) actieveWacht = null;
    clearInterval(interval);
    document.removeEventListener('visibilitychange', bijZicht);
    window.removeEventListener('focus', ververs);
  };
}
